/**
 * Starting a table, one step at a time.
 *
 * The endpoints for this have existed and been tested since Phase 3, and
 * nothing on the client reached them — so the only ways to create a campaign
 * were the demo seed and a hand-rolled API call. For a private game whose
 * whole point is a known group sitting down together, that is the first thing
 * a new table needs and the one thing it could not do.
 *
 * ⚠ **The order is real, enforced at both ends, and used to be stated
 * nowhere** — and the original docstring was itself the evidence. It said "two
 * steps: name the table and write the premise, then generate the world", never
 * mentioning the character that was then a precondition of step one. The
 * comment described the flow the author had in mind; the code implemented a
 * different one.
 *
 * That was fixed by numbering the sections, which said what the order *was*
 * without making the order happen — every field stayed on screen at once.
 * **This is the step-by-step version**, built on the shared `Wizard` shell
 * so the character flow and this one behave the same way.
 *
 * ⚠ **One continuous step list.** Creation and generation still fail
 * differently — a refused campaign is a validation problem the player can
 * fix, a refused generation is the model being unreachable — but that is
 * enforced by which step's own button fires which network call (`premise`'s
 * own advancing control creates the row, `map`'s Set, `ground`'s Import,
 * `world`'s Generate), not by two separate wizards ending in a handoff. "Go
 * to the table" on the last step now genuinely waits on all three: a
 * generated world, an uploaded map, and its scale — see
 * `campaignWizardSteps` for why `open` used to skip the first of those.
 *
 * ⚠ **`map` is split into `map` and `ground`, there is no character step,
 * and `review` is folded into `premise`.** The combined map step used to ask
 * for a picture, a scale and a terrain import in one screen —
 * `campaignWizardSteps` splits the last of those out into its own step —
 * this component no longer offers a character picker at all, and the old
 * `review` step's only real job (firing `POST /api/campaigns`) now lives on
 * Premise's own advancing button, styled identically to a plain Next
 * because which button happens to also write a row is not something a
 * player needs to know.
 *
 * **Two routes, one component.** `campaigns/new` starts blank. Creating a
 * campaign navigates to `campaigns/:campaignId/setup`, which hydrates every
 * field from `GET /api/campaigns/:campaignId` — the same component, so a
 * reload mid-setup resumes from server truth instead of losing progress that
 * only ever lived in local state.
 */

import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { ApiError, api } from '../api.js';
import { readDraft, writeDraft } from './campaignDraft.js';
import {
  MAP_IMAGE_TYPES,
  MAP_IMAGE_TYPES_LABEL,
  MAX_MAP_UPLOAD_LABEL,
  uploadMapImage,
} from '../map/uploadMap.js';
import { TerrainLayer } from '../map/TerrainLayer.js';
import { terrainTally, type TerrainGridView } from '../map/terrain-palette.js';
import { WorldImport, type ImportVocabulary } from '../map/WorldImport.js';
import { furthestReachable, Wizard } from './Wizard.js';
import { campaignWizardSteps, type CampaignWizardState } from './campaignWizard.js';
import { EnterCampaign, isSeatedAt } from './EnterCampaign.js';

/**
 * A unit conversion, not game data — the server only ever stores and asks
 * for miles (`worldMapScaleMiles`; every travel rule this application reads
 * is in miles). Azgaar's own on-screen scale bar can be in either unit
 * depending on how that map was configured, so the Owner needs to say which
 * one they're reading off before it goes to the server.
 */
const MILES_PER_KM = 0.621371;

interface CreatedCampaign {
  campaign: { id: string; name: string };
}

interface CampaignRow {
  id: string;
  name: string;
  premise: string;
  createdBy: string | null;
  worldMapKey: string | null;
  worldState: { worldMapScaleMiles?: number; worldSeeded?: boolean };
}

const field =
  'rounded-md border border-neutral-300 bg-white px-3 py-2 text-sm dark:border-neutral-700 dark:bg-neutral-900';

/**
 * A native `<input type="file">` with no styling renders its trigger as bare
 * OS chrome — "Browse…"/"Choose File" text with no border, sitting beside
 * buttons that all have one. Tailwind's `file:` variant styles the browser's
 * own pseudo-element rather than replacing the control, so the picker stays
 * native (keyboard, drag-drop, screen readers) while looking like every
 * other button on the page.
 */
const fileInput =
  'text-xs file:mr-3 file:rounded-md file:border file:border-neutral-300 file:bg-white ' +
  'file:px-3 file:py-1 file:text-sm file:font-medium file:text-neutral-900 ' +
  'hover:file:bg-neutral-50 dark:file:border-neutral-700 dark:file:bg-neutral-900 ' +
  'dark:file:text-neutral-100 dark:hover:file:bg-neutral-800';

/** How long "Suggest one" stays disabled after a press. */
const PREMISE_SUGGESTION_COOLDOWN_MS = 30_000;

/**
 * "1,842 cells mapped — grasslandPlains, forestMedium, hillsRolling."
 *
 * `terrainTally` already sorts biggest first; three names is enough to
 * confirm "yes, that's the region I meant" without turning the summary line
 * into the whole legend.
 */
function terrainSummary(grid: TerrainGridView): string {
  const tally = terrainTally(grid);
  const mapped = tally.reduce((sum, t) => sum + t.cells, 0);
  const names = tally
    .slice(0, 3)
    .map((t) => t.name)
    .join(', ');
  return `${mapped} cells mapped — ${names || 'nothing recognisable'}.`;
}

export function NewCampaignPage() {
  const navigate = useNavigate();
  // Present only on `campaigns/:campaignId/setup` — absent on `campaigns/new`.
  const { campaignId: resumeId } = useParams();
  // **Restored from the draft, not blank.** Leaving this page for another
  // and back is a real path (the premise example, the map generator), and a
  // form that emptied itself while you obeyed it would be a worse offer than
  // no link at all. `useState` with an initialiser runs once, which is what
  // makes this the first paint rather than a flash of empty fields.
  const [name, setName] = useState(() => (resumeId ? '' : readDraft().name));
  const [premise, setPremise] = useState(() => (resumeId ? '' : readDraft().premise));
  const [at, setAt] = useState(0);
  const [created, setCreated] = useState<CreatedCampaign['campaign'] | null>(null);
  // ⚠ **Only set on `campaigns/:campaignId/setup`.** A fresh `campaigns/new`
  // has nothing to fetch — `created` starts and stays `null` there, and
  // `create()` below navigates away rather than ever setting it on this
  // instance, so the merged step list only ever needs to trust the server
  // once, on the route that names an id.
  const [hydrating, setHydrating] = useState(resumeId !== undefined);
  const [busy, setBusy] = useState(false);
  /**
   * Whether the Owner already holds a seat at their own table.
   *
   * ⚠ **Why this exists at all.** `open`'s own `done` never asked this — the
   * button enabled the instant the world was ready and only found out there
   * was no seat *after* navigating, when `RequireSeat` intercepted it with
   * the identical picker rendered below. Making a character and making a
   * campaign are independent acts, so an unseated Owner reaching this step
   * is the normal case, not an edge one.
   */
  const [seatedHere, setSeatedHere] = useState<'checking' | 'yes' | 'no'>('checking');
  const [premiseNote, setPremiseNote] = useState<string | null>(null);
  // ⚠ **A model call with no cooldown at all.** `busy` only ever stopped
  // two presses overlapping — the instant one reply landed the button was
  // live again, so a bored click could queue a model call every few seconds
  // with nothing to say it shouldn't. `readyAt`
  // is a wall-clock deadline, not a counter, so a slow tab (a backgrounded
  // browser tab throttles timers) still reads the right remaining time on its
  // next render rather than one a suspended interval never counted down.
  const [readyAt, setReadyAt] = useState<number | null>(null);
  const [now, setNow] = useState(() => Date.now());

  // Ticks once a second only while a cooldown is actually running, so the
  // countdown text updates — and stops itself the moment the deadline
  // passes, rather than a page-lifetime interval nothing ever clears.
  useEffect(() => {
    if (readyAt === null) return;
    const id = setInterval(() => {
      if (Date.now() >= readyAt) {
        setReadyAt(null);
        return;
      }
      setNow(Date.now());
    }, 1000);
    return () => clearInterval(id);
  }, [readyAt]);

  const cooldownSecondsLeft =
    readyAt !== null ? Math.max(0, Math.ceil((readyAt - now) / 1000)) : 0;
  const [error, setError] = useState<string | null>(null);
  const [seedNote, setSeedNote] = useState<string | null>(null);
  const [seeded, setSeeded] = useState(false);
  // **A map is part of setup, not an afterthought.** Both halves are required
  // before the table opens: the image is what the players look at, and the
  // scale is what makes it a map the *rules* can read. Either alone is half a
  // map — an image with no scale costs no journey anything, silently.
  const [mapped, setMapped] = useState(false);
  const [scaleMiles, setScaleMiles] = useState('');
  // **What unit the box's own number is in — not what gets stored.** The
  // server only ever holds miles; this only decides whether `putScale`
  // converts what was typed before sending it. Defaults to miles rather than
  // remembering a past choice, because there is nothing to remember it
  // from — `worldMapScaleMiles` comes back from the server already
  // converted, with no record of which unit the Owner originally read it in.
  const [scaleUnit, setScaleUnit] = useState<'miles' | 'km'>('miles');
  const [scaleSet, setScaleSet] = useState(false);
  const [mapNote, setMapNote] = useState<string | null>(null);
  // **A plain picture carries no terrain data at all.** World generation now
  // reads the real, imported ground to place regions and settlements with
  // actual coordinates. Read from `GET .../world-import`'s own `imported`
  // field, which always 200s — see the hydration effect below for why that
  // matters here and did not for `MapSetupPage`'s own `GET .../terrain` check.
  const [terrainImported, setTerrainImported] = useState(false);
  // **The live preview.** Fetched only once `terrainImported` is known true —
  // `GET .../terrain` 404s for "nothing imported yet", and every browser logs
  // a 404 as a console error whether or not the caller handles it. `null`
  // means either "not imported" or "the preview failed to load"; both draw
  // nothing, which is the safe direction for a picture that is decoration.
  const [terrainGrid, setTerrainGrid] = useState<TerrainGridView | null>(null);

  // Gated on readiness rather than on which step is currently showing, so
  // there is no loading flash the moment somebody actually reaches "Open the
  // table" having already been sitting on an earlier step.
  useEffect(() => {
    if (!created || !(seeded && mapped && scaleSet)) return;
    let current = true;
    void api
      .get<{ characters: { seat?: { campaignId: string } }[] }>('/api/characters')
      .then((body) => {
        if (current) setSeatedHere(isSeatedAt(body.characters, created.id) ? 'yes' : 'no');
      })
      .catch(() => {
        if (current) setSeatedHere('no');
      });
    return () => {
      current = false;
    };
  }, [created, seeded, mapped, scaleSet]);

  /**
   * ⚠ **The reload-loses-progress fix.** Everything about setup used to be
   * local, optimistic state — flipped once after each request succeeded, and
   * forgotten the instant the tab closed. This is the one read that makes
   * `campaigns/:campaignId/setup` resumable: the server row already carries
   * every signal (`worldMapKey`, `worldState.worldSeeded`,
   * `worldState.worldMapScaleMiles`), so a returning Owner lands back exactly
   * where the campaign's own truth says they are, not where local memory
   * last left off.
   */
  useEffect(() => {
    if (!resumeId) return;
    let current = true;
    setHydrating(true);
    // **The terrain check rides alongside the campaign read, not after it.**
    // `setAt` below needs `terrainImported` to compute the right furthest
    // step on the very first paint — read afterwards, a campaign that had
    // already imported terrain would briefly (or, since nothing re-runs
    // `setAt`, permanently) show the wizard stuck on `map`.
    //
    // ⚠ **`world-import`, not `terrain`.** `MapSetupPage.refresh()`'s own
    // check reads `GET .../terrain`, which answers 404 for "nothing
    // imported yet" — the *correct* answer, but every browser logs a
    // network 404 as a console error regardless of whether the caller
    // handles it, which is not a real failure here and would poison a
    // console-clean assertion on the very first visit to this step.
    // `world-import` answers the same question and always 200s
    // (`imported: null` for "nothing yet").
    void Promise.all([
      api.get<{ campaign: CampaignRow }>(`/api/campaigns/${resumeId}`),
      api
        .get<ImportVocabulary>(`/api/campaigns/${resumeId}/world-import`)
        .then((v) => v.imported !== null)
        .catch(() => false),
    ])
      .then(([body, terrainOk]) => {
        if (!current) return;
        const c = body.campaign;
        const hydrated: CampaignWizardState = {
          name: c.name,
          premise: c.premise,
          seeded: c.worldState.worldSeeded === true,
          mapped: c.worldMapKey !== null,
          scaleSet: c.worldState.worldMapScaleMiles !== undefined,
          terrainImported: terrainOk,
        };
        setCreated({ id: c.id, name: c.name });
        setName(hydrated.name);
        setPremise(hydrated.premise);
        setSeeded(hydrated.seeded);
        // A reload after a *previous* visit's own generate — that response
        // is long gone, so without this the step's own summary line renders
        // with nothing in front of "Review it on the map before play."
        if (hydrated.seeded) setSeedNote('The world has been written.');
        setMapped(hydrated.mapped);
        setScaleSet(hydrated.scaleSet);
        setTerrainImported(hydrated.terrainImported);
        if (c.worldState.worldMapScaleMiles !== undefined) {
          setScaleMiles(String(c.worldState.worldMapScaleMiles));
        }
        setAt(furthestReachable(campaignWizardSteps(hydrated)));
        // The grid itself, only once terrain is known to exist — the same
        // 404-avoidance the boolean check above already applies.
        if (terrainOk) {
          void api
            .get<{ grid: TerrainGridView }>(`/api/campaigns/${resumeId}/terrain`)
            .then((r) => {
              if (current) setTerrainGrid(r.grid);
            })
            .catch(() => undefined);
        }
      })
      .catch(() => setError('could not load this campaign'))
      .finally(() => {
        if (current) setHydrating(false);
      });
    return () => {
      current = false;
    };
  }, [resumeId]);

  // Kept on every keystroke rather than on unmount: leaving this page is a
  // navigation the router may not tell us about, and a draft that only saved
  // on a clean exit would be lost by exactly the click it exists for.
  // ⚠ **Only before creation.** Once `created` exists this is no longer a
  // draft of something that might never be made — it is the record of
  // something that already was, and the name/premise fields are read-only
  // (see the `name`/`premise` steps' own `disabled={created !== null}`) for
  // exactly that reason.
  useEffect(() => {
    if (created) return;
    writeDraft({ name, premise });
  }, [name, premise, created]);

  const create = () => {
    setBusy(true);
    setError(null);
    void api
      .post<CreatedCampaign>('/api/campaigns', {
        name: name.trim(),
        premise: premise.trim(),
      })
      .then((body) => {
        // The campaign exists, so the draft has served its purpose. Left
        // behind, it would repopulate the form the next time somebody started
        // a table and offer them the last one's name.
        writeDraft(null);
        // ⚠ **Navigates rather than setting local state.** The remounted
        // `campaigns/:campaignId/setup` instance rehydrates from the server
        // in the effect above, which is the one code path that has to be
        // correct for both "just created" and "resumed a week later" —
        // setting `created` here too would be a second, parallel path that
        // could drift from it.
        navigate(`/campaigns/${body.campaign.id}/setup`, { replace: true });
      })
      // The server's own words: it knows which rule was broken and this does
      // not, and paraphrasing would be a second, worse copy of the rules.
      .catch((e: unknown) =>
        setError(e instanceof ApiError ? e.message : 'the campaign was refused'),
      )
      .finally(() => setBusy(false));
  };

  const putMap = (file: File) => {
    if (!created) return;
    setBusy(true);
    setMapNote(`uploading ${file.name}…`);
    // ⚠ **Two calls, not one.** The upload only ever wrote an `assets` row —
    // `campaigns.world_map_key` (what actually "activates" a map) was
    // previously reachable only from `host:set_world_map`, a live table's
    // own socket panel a not-yet-open campaign's Owner can never reach. This
    // is the fix: activate over HTTP, right after the upload that made the
    // key exist.
    void uploadMapImage(created.id, 'world_map', file)
      .then((key) =>
        api.put(`/api/campaigns/${created.id}/world-map`, { key }).then(() => {
          setMapped(true);
          setMapNote(`${file.name} is the world map.`);
        }),
      )
      .catch((e: unknown) =>
        setMapNote(e instanceof ApiError ? e.message : 'that image was refused'),
      )
      .finally(() => setBusy(false));
  };

  const putScale = () => {
    if (!created) return;
    const typed = Number(scaleMiles);
    if (!Number.isFinite(typed) || typed <= 0) {
      setMapNote(`the scale is a positive number of ${scaleUnit === 'km' ? 'kilometres' : 'miles'}`);
      return;
    }
    // Converted here, once, on the way out — the server's `scaleMiles` field
    // and every travel rule that reads it stay in miles regardless of which
    // unit the Owner typed the map's own number in as.
    const miles = scaleUnit === 'km' ? typed * MILES_PER_KM : typed;
    setBusy(true);
    void api
      .put(`/api/campaigns/${created.id}/map-scale`, { scaleMiles: miles })
      .then(() => {
        setScaleSet(true);
        setMapNote(`The map is ${Math.round(miles * 10) / 10} miles across.`);
      })
      .catch((e: unknown) =>
        setMapNote(e instanceof ApiError ? e.message : 'that scale was refused'),
      )
      .finally(() => setBusy(false));
  };

  /** Re-reads whether terrain has been imported, after `WorldImport` writes some. */
  const refreshTerrain = () => {
    if (!created) return;
    void api
      .get<ImportVocabulary>(`/api/campaigns/${created.id}/world-import`)
      .then((v) => v.imported !== null)
      .catch(() => false)
      .then((ok) => {
        setTerrainImported(ok);
        if (!ok) {
          setTerrainGrid(null);
          return;
        }
        void api
          .get<{ grid: TerrainGridView }>(`/api/campaigns/${created.id}/terrain`)
          .then((r) => setTerrainGrid(r.grid))
          .catch(() => setTerrainGrid(null));
      });
  };

  const generateWorld = () => {
    if (!created) return;
    setBusy(true);
    setSeedNote(null);
    void api
      // ⚠ **The server has never sent a `created` field** — it answers
      // `written`/`offered` (the count is reported honestly: names already
      // present are skipped rather than failing the request, so "4 of 7" is
      // a real answer and a silent 7 would be a lie). Reading a field that
      // was never there put a literal "undefined" in front of a player, on
      // the one screen that reports an irreversible action.
      .post<{ written: number; offered: number }>(
        `/api/campaigns/${created.id}/canon/generate`,
        {},
      )
      .then((body) => {
        setSeeded(true);
        setSeedNote(
          `The world is written: ${body.written} of ${body.offered} entries` +
            (body.written < body.offered ? ' (the rest named something already there)' : '') +
            '.',
        );
      })
      .catch((e: unknown) =>
        // A refusal here is survivable and **retryable**: the commonest cause
        // is the model being unreachable, which is exactly the case you would
        // try again — a failed attempt never sets `worldSeeded`, so the
        // server has nothing to refuse a second time. Only a *success* closes
        // the button, because that is the one the server will refuse to redo.
        setSeedNote(e instanceof ApiError ? e.message : 'the world generator is unavailable'),
      )
      .finally(() => setBusy(false));
  };

  if (hydrating) return <p className="text-sm">Loading…</p>;

  const wizardState: CampaignWizardState = {
    name,
    premise,
    seeded,
    mapped,
    scaleSet,
    terrainImported,
  };
  const steps = campaignWizardSteps(wizardState);
  const here = steps[at]?.key;

  return (
    <div className="space-y-5">
      <h1 className="text-2xl font-semibold tracking-tight">
        {created ? created.name : 'Start a campaign'}
      </h1>

      {error && (
        <p role="alert" className="text-sm text-red-700 dark:text-red-400">
          {error}
        </p>
      )}

      <Wizard
        steps={steps}
        current={at}
        onGo={setAt}
        finish={{
          label: 'Go to the table',
          onPress: () => navigate(`/campaigns/${created!.id}`),
          busy,
        }}
      >
        {here === 'name' && (
          <div className="space-y-2">
            <label className="sr-only" htmlFor="campaign-name">
              Campaign name
            </label>
            <input
              id="campaign-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={created !== null}
              className={`w-full max-w-md ${field} disabled:opacity-60`}
            />
            {/* ⚠ **A new failure mode the merge introduced.** Once the row
                exists this field stays reachable (every finished step does)
                but there is no route to change a campaign's name — editing it
                here would silently do nothing. Read-only, said plainly,
                rather than a box that looks live and is not. */}
            {created && (
              <p className="text-xs text-neutral-600 dark:text-neutral-300">
                Saved when the table was created.
              </p>
            )}
          </div>
        )}

        {here === 'premise' && (
          <div className="space-y-2">
            {/* The premise is the whole input to world generation, and generating
                from nothing produces a generic fantasy world — so a table made
                without one can never have a generated one. */}
            <p className="text-xs text-neutral-600 dark:text-neutral-300">
              One paragraph. This is the entire input the world generator gets, so a campaign with
              no premise cannot generate one — and generation runs only once, so there is no
              adding it afterwards.
            </p>
            <textarea
              id="campaign-premise"
              value={premise}
              onChange={(e) => setPremise(e.target.value)}
              disabled={created !== null}
              rows={8}
              aria-label="Premise"
              placeholder="The salt marsh drowned the lower town two winters ago, and what came up the channels after it has not left."
              className={`w-full max-w-2xl ${field} disabled:opacity-60`}
            />
            {created ? (
              <p className="text-xs text-neutral-600 dark:text-neutral-300">
                Saved when the table was created.
              </p>
            ) : (
              /* ⚠ **A suggestion to edit, never a value to use.** The premise
                  is the whole input to world generation, a run that cannot be
                  repeated, so it has to stay the player's — this unblocks a
                  blank page and nothing more.

                  **Generated fresh per press rather than shipped as a canned
                  example**, because an example gets copied into the field it
                  illustrates: this project has been bitten by that three times, and a
                  static premise would be pasted verbatim into real campaigns.

                  ⚠ It **replaces** what is in the box, so it is refused once there is
                  something to lose — overwriting a paragraph somebody wrote is a far
                  worse failure than making them clear the box first. */
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  disabled={busy || premise.trim() !== '' || cooldownSecondsLeft > 0}
                  title={
                    premise.trim() !== ''
                      ? 'clear the box first — this would replace what you have written'
                      : cooldownSecondsLeft > 0
                        ? `one call to the DM at a time — ${cooldownSecondsLeft}s left`
                        : undefined
                  }
                  onClick={() => {
                    setBusy(true);
                    setPremiseNote(null);
                    void api
                      .post<{ premise: string }>('/api/campaigns/premise-example', {})
                      .then((body) => setPremise(body.premise))
                      // A failure leaves the box alone and says so. Unlike world
                      // generation nothing is lost, so it is worth pressing again.
                      .catch(() =>
                        setPremiseNote('the DM had nothing to suggest just now — try again'),
                      )
                      .finally(() => {
                        setBusy(false);
                        // ⚠ **The cooldown starts on every press, success or
                        // failure alike.** A failure is still a call that went
                        // to the model — the request is what costs, not the
                        // answer — so waiving it on failure would make "spam it
                        // until it works" the fast path.
                        const deadline = Date.now() + PREMISE_SUGGESTION_COOLDOWN_MS;
                        setReadyAt(deadline);
                        setNow(Date.now());
                      });
                  }}
                  className="flex items-center gap-2 rounded-md border border-neutral-300 px-3 py-1 text-sm disabled:opacity-50 dark:border-neutral-700"
                >
                  {/* ⚠ **Nothing said this was working except the button
                      going grey.** A CSS spinner rather than static
                      "Generating…" text, because the wait is long enough (a
                      live model call) that motion is what tells a player it
                      has not silently died. */}
                  {busy && (
                    <span
                      aria-hidden="true"
                      className="h-3 w-3 animate-spin rounded-full border-2 border-neutral-400 border-t-transparent dark:border-neutral-500"
                    />
                  )}
                  {busy ? 'Asking the DM…' : 'Suggest one'}
                </button>
                {/* ⚠ **The cooldown really does clear — but the button's
                    OTHER disable reason**, "there is already something in
                    the box," is true the instant a suggestion lands there,
                    so the two conditions overlap for the one case that
                    matters most: right after a successful press. The
                    `title` tooltip already explained this; nothing said so
                    without hovering a disabled button, which reads exactly
                    like "still broken." */}
                {!busy && cooldownSecondsLeft === 0 && premise.trim() !== '' && (
                  <span className="text-xs text-neutral-600 dark:text-neutral-300">
                    Clear the box to suggest something new.
                  </span>
                )}
                {!busy && cooldownSecondsLeft > 0 && (
                  <span className="text-xs text-neutral-600 dark:text-neutral-300">
                    ready again in {cooldownSecondsLeft}s
                  </span>
                )}
                {premiseNote && (
                  <span className="text-xs text-neutral-600 dark:text-neutral-300">
                    {premiseNote}
                  </span>
                )}
              </div>
            )}

            {/* ⚠ **This step's own advancing control, styled to be invisible
                as one.** The old `review` step was a whole screen just to
                echo Name and Premise back and press a button; that a button
                also creates a row is not something a player needs to know,
                so this one looks and behaves exactly like a plain Next. The
                first press fires `POST /api/campaigns`; on a resumed session
                (`created` already set) it just moves on.
                Deliberately kept early rather than deferred to "Open the
                table": the picture, the terrain import and generation are
                each their own server write, not form fields, and creating
                the row now is what makes a reload mid-setup resumable at
                all — see `campaignWizard.ts` for the fuller reasoning. */}
            <button
              type="button"
              onClick={created ? () => setAt(at + 1) : create}
              disabled={busy}
              className="rounded-md border border-neutral-300 px-4 py-2 text-sm font-medium disabled:opacity-40 dark:border-neutral-700"
            >
              Next
            </button>
          </div>
        )}

        {here === 'map' && (
          <div className="space-y-3">
            {/* ⚠ **Trimmed hard, twice now.** The ground import used to
                live here too — it is its own step now, so this one is down
                to what it actually says: a picture, and its scale. */}
            <p className="text-xs text-neutral-600 dark:text-neutral-300">
              A world map, and how many miles wide it is — the picture is what your players
              read, the scale is what every journey, day of travel and getting-lost check is
              measured against.
            </p>

            {/* ⚠ **Written out, not linked to.** The old copy just said
                "export the image", which assumed a usable map already
                existed. This is the missing step before it — and
                deliberately visible rather than behind a `<summary>`: it's
                read once, by everyone, right before the control it
                explains. */}
            <div className="space-y-2 rounded-md border border-emerald-200 bg-emerald-50 p-3 dark:border-emerald-900 dark:bg-emerald-950/30">
              <p className="text-xs font-medium text-emerald-800 dark:text-emerald-300">
                How to generate one worth playing on
              </p>
              <ol className="list-decimal space-y-1.5 pl-4 text-xs text-neutral-700 dark:text-neutral-300">
                <li>
                  <a
                    href="https://azgaar.github.io/Fantasy-Map-Generator/"
                    target="_blank"
                    rel="noreferrer"
                    className="underline underline-offset-2"
                  >
                    Azgaar&rsquo;s Fantasy Map Generator ↗
                  </a>{' '}
                  — it draws a world the moment it opens.
                </li>
                <li>
                  Regenerate until the shape fits where your party will actually go. A whole
                  continent is harder to run than a region they can cross in a campaign —
                  smaller plays better than bigger.
                </li>
                <li>
                  Happy with it? Export the picture (PNG or JPEG), and read its width off
                  Azgaar&rsquo;s own scale bar — the box below takes either miles or kilometres, so
                  just note the number and which one it is.
                </li>
              </ol>
              <p className="text-xs text-neutral-500 dark:text-neutral-400">
                Any other generator&rsquo;s picture works here too, and so does a drawing — only
                Azgaar&rsquo;s own export carries the ground data the next step needs.{' '}
                <Link to="/guide" className="underline underline-offset-2">
                  The guide has the longer version
                </Link>
                .
              </p>
            </div>

            {/* ⚠ **This was an `sr-only` label on a bare file button.** The
                step said why a map mattered and never what to give it — no
                formats, no size, and nothing at all for the commonest state a
                new table is in, which is not having a map. */}
            <div className="space-y-1">
              <label className="block text-sm font-medium" htmlFor="campaign-map-image">
                The picture
              </label>
              <p className="text-xs text-neutral-600 dark:text-neutral-300">
                {MAP_IMAGE_TYPES_LABEL}, up to {MAX_MAP_UPLOAD_LABEL}.
              </p>
              <input
                id="campaign-map-image"
                type="file"
                accept={MAP_IMAGE_TYPES}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) putMap(file);
                }}
                className={`block ${fileInput}`}
              />
            </div>

            <div className="space-y-1">
              <label className="block text-sm font-medium" htmlFor="campaign-scale">
                How wide across
              </label>
              {/* **What "across" means**, because the box previously offered a
                  placeholder and no unit of meaning. */}
              <p className="text-xs text-neutral-600 dark:text-neutral-300">
                The left edge of the image to the right edge — it does not have to be exact, only
                what your table agrees the map covers. Type the number Azgaar showed you, in
                whichever unit it showed it in — this application's own rules run in miles, and
                the conversion happens for you.
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <input
                id="campaign-scale"
                value={scaleMiles}
                onChange={(e) => setScaleMiles(e.target.value)}
                inputMode="numeric"
                placeholder="240"
                className="w-24 rounded-md border border-neutral-300 bg-white px-2 py-1 text-sm dark:border-neutral-700 dark:bg-neutral-900"
              />
              <select
                aria-label="Unit"
                value={scaleUnit}
                onChange={(e) => setScaleUnit(e.target.value === 'km' ? 'km' : 'miles')}
                className="rounded-md border border-neutral-300 bg-white px-2 py-1 text-sm dark:border-neutral-700 dark:bg-neutral-900"
              >
                <option value="miles">miles</option>
                <option value="km">km</option>
              </select>
              <button
                type="button"
                onClick={putScale}
                disabled={busy || scaleMiles.trim() === ''}
                className="rounded-md border border-neutral-300 px-3 py-1 text-sm disabled:opacity-50 dark:border-neutral-700"
              >
                Set
              </button>
            </div>
            {mapNote && <p className="text-sm">{mapNote}</p>}
          </div>
        )}

        {here === 'ground' && (
          <div className="space-y-3">
            {/* **Its own step, not a third field on `map`.** A plain uploaded
                picture carries no biome or terrain data at all, so generation
                cannot read it — only the Azgaar GeoJSON import does, and
                importing it is unrelated work to uploading a picture. */}
            <p className="text-xs text-neutral-600 dark:text-neutral-300">
              The real terrain under your map — what world generation reads to place regions
              and settlements for real, instead of leaving them for you to pin by hand.
            </p>

            <div className="space-y-2 rounded-md border border-emerald-200 bg-emerald-50 p-3 dark:border-emerald-900 dark:bg-emerald-950/30">
              <p className="text-xs font-medium text-emerald-800 dark:text-emerald-300">
                How to export it
              </p>
              <ol className="list-decimal space-y-1.5 pl-4 text-xs text-neutral-700 dark:text-neutral-300">
                <li>
                  In the same generator:{' '}
                  <strong>Tools → Export → &ldquo;Export to GeoJSON&rdquo;</strong>.
                </li>
                <li>
                  Click <strong>Cells</strong> — carries the height and biome data. It&rsquo;s
                  the one file this step actually needs.
                </li>
                <li>
                  Click <strong>Rivers</strong> and <strong>Routes</strong> too, if you want
                  river crossings and roads to matter for travel — both are optional.
                </li>
              </ol>
            </div>

            <WorldImport campaignId={created!.id} onImported={refreshTerrain} compact />

            {/* ⚠ **Answers "did that actually work?" without a second
                screen.** `TerrainLayer` is the same canvas the live table
                paints; `opacity={1}` because here it is the whole picture,
                not a wash over an uploaded drawing the way it is there. */}
            {terrainGrid && (
              <div className="space-y-1 border-t border-neutral-200 pt-3 dark:border-neutral-800">
                <p className="text-xs font-medium text-emerald-700 dark:text-emerald-400">
                  Imported — here&rsquo;s what it read
                </p>
                <div className="relative aspect-[16/7] overflow-hidden rounded-md border border-neutral-200 dark:border-neutral-800">
                  <TerrainLayer grid={terrainGrid} opacity={1} />
                </div>
                <p className="text-xs text-neutral-500 dark:text-neutral-400">
                  {terrainSummary(terrainGrid)}
                </p>
              </div>
            )}
          </div>
        )}

        {here === 'world' && (
          <div className="space-y-2">
            {/* ⚠ **Trimmed to the one sentence that changes what you do.**
                It used to also explain *why* generation is ordered after the
                map and the ground — true, and irrelevant here: by the time
                anyone reads this, those steps are already behind them, so
                the reason changes nothing about what to do next. "Runs
                once" stays, because it is the one fact that bears on
                pressing the button below. */}
            <p className="text-xs text-neutral-600 dark:text-neutral-300">
              Writes places, people and factions from your premise. It runs <strong>once</strong>{' '}
              — a second run would invent a different world alongside the first, so the server
              refuses it.
            </p>
            {premise.trim() === '' ? (
              // ⚠ **A button that can only 400 is worse than no button.** The
              // premise step is optional to create, `world` is not optional to
              // reach "Go to the table" — so a campaign made without one hits a
              // real dead end here, and the honest answer is to send the
              // player back to fill it in, not to offer a press that fails.
              <p className="text-sm text-amber-800 dark:text-amber-300">
                There is no premise yet, and the world generator needs one — go back and write
                one first.
              </p>
            ) : seeded ? (
              // ⚠ **No longer a disabled "Try again."** A label offering a
              // retry that can never fire is worse than no button, the same
              // reasoning the premise-not-set amber notice above already
              // follows. A *successful* run is the one the server will
              // actually refuse to redo, so once
              // `seeded` is true there is nothing left to press — only
              // somewhere to go look at what was written.
              <p className="text-sm">
                {seedNote} Review it on{' '}
                <Link
                  to={`/campaigns/${created!.id}/map`}
                  className="underline underline-offset-2"
                >
                  the map
                </Link>{' '}
                before play.
              </p>
            ) : (
              <>
                <button
                  type="button"
                  onClick={generateWorld}
                  disabled={busy}
                  className="rounded-md border border-neutral-300 px-3 py-1 text-sm disabled:opacity-50 dark:border-neutral-700"
                >
                  {busy ? 'Writing…' : seedNote ? 'Try again' : 'Generate'}
                </button>
                {seedNote && (
                  <p className="text-sm text-amber-800 dark:text-amber-300">{seedNote}</p>
                )}
              </>
            )}
          </div>
        )}

        {here === 'open' && (
          <div className="space-y-2">
            {/* ⚠ **The old "ground has no cost yet" warning is gone, on
                purpose.** It used to be genuinely possible to reach this step
                with no terrain imported; now `ground` is its own required
                step before this one, so reaching `open` at all already means
                a real import happened. A warning that can no longer fire
                truthfully is worse than none — it reads as the table still
                being broken. */}
            <p className="text-sm text-neutral-600 dark:text-neutral-300">
              {seeded && mapped && scaleSet
                ? 'Everything the table needs to open is in place.'
                : 'The table will open once it has a generated world, a map and its ground — the steps before this.'}
            </p>
            {/* ⚠ **"Go to the table" used to enable the moment the world was
                ready and say nothing about a seat** — the Owner found out
                only after the click, when `RequireSeat` showed this
                identical picker on its own page. Embedded here instead,
                under `created`'s own id, so the wizard's last step answers
                the one thing actually stopping "Go to the table" from
                working rather than leaving it to be discovered. */}
            {seeded && mapped && scaleSet && created && seatedHere === 'no' && (
              <EnterCampaign
                campaignId={created.id}
                onJoined={() => setSeatedHere('yes')}
                heading={false}
              />
            )}
          </div>
        )}
      </Wizard>
    </div>
  );
}
